/**
 * Bentonite lubrication telemetry types. Sourced from the Herrenknecht
 * /api/v3/Bentonite/values endpoint, whose schema we confirmed against
 * /api/v3/Bentonite/metadata on container m02208c on 2026-05-05.
 *
 *   geo     - Geology characteristic (integer code)
 *   igv     - Initial grouting volume per event (litres)
 *   agv     - Additional grouting volume per event (litres)
 *   b1      - Distribution valve 1 state (integer)
 *   b2      - Distribution valve 2 state (integer)
 *   b3      - Distribution valve 3 state (integer)
 *   skf     - Skin friction (kN/m)
 *   desVol  - Bentonite target cumulative amount (m^3)
 *   actVol  - Bentonite actual cumulative amount (m^3)
 *
 * All records are sequence-keyed (id) and time-stamped (lastUpdated).
 *   GET /Bentonite/values                          -> all records
 *   GET /Bentonite/values/{from}                   -> records >= {from}
 *   GET /Bentonite/values/{from}/{to}              -> records in [{from},{to}]
 * No date-based query parameters; we filter by lastUpdated client-side.
 */

export interface BentoniteRecord {
  uuid: string;
  /** Sequence id from the Herrenknecht endpoint. Used as cursor + display. */
  seqId: number;
  /** Event timestamp (ISO). */
  ts: string;

  /** Geology characteristic at firing time. Integer soil class code. */
  geo: number | null;
  /** Initial grouting volume for this event (litres). */
  igvL: number | null;
  /** Additional grouting volume for this event (litres). */
  agvL: number | null;
  /** Distribution valve 1 state. */
  b1: number | null;
  /** Distribution valve 2 state. */
  b2: number | null;
  /** Distribution valve 3 state. */
  b3: number | null;
  /** Measured skin friction (kN/m). */
  skfKnM: number | null;
  /** Target cumulative bentonite (m^3). */
  desVolM3: number | null;
  /** Actual cumulative bentonite (m^3). */
  actVolM3: number | null;

  // ────────────────────────────────────────────────────────────────
  // Fields populated by manual CSV ingestion (older DAQ machines).
  // Optional; null when sourced from the /Bentonite report endpoint.
  // ────────────────────────────────────────────────────────────────
  /** Cutterhead chainage at firing time, metres. */
  chainageM?: number | null;
  /** Bentonite line / circuit number (Bentonitlinie). */
  lineNo?: number | null;
  /** Distribution box number (V23_KastenNr_akt_Z). */
  boxNo?: number | null;
  /** Active valve in the box (1, 2, or 3 — V23_Ventil_Kasten_akt_Z). */
  valveNo?: number | null;
  /** How long the active valve was open this visit, seconds. */
  valveOpenS?: number | null;
  /** Bentonite line pressure, bar. */
  linePressureBar?: number | null;
  /** Bentonite pump pressure, bar. */
  pumpPressureBar?: number | null;
}

/** Convenience: which of b1/b2/b3 is currently 'active' (non-zero). */
export function activeValveNo(
  r: Pick<BentoniteRecord, "b1" | "b2" | "b3">,
): 1 | 2 | 3 | null {
  if (r.b1 != null && r.b1 !== 0) return 1;
  if (r.b2 != null && r.b2 !== 0) return 2;
  if (r.b3 != null && r.b3 !== 0) return 3;
  return null;
}

export type ValveNo = 1 | 2 | 3;
export const VALVE_NUMBERS: ValveNo[] = [1, 2, 3];

/** Bin of bentonite events by cutterhead chainage at firing time. */
export interface PerChainageRow {
  /** Bin midpoint in metres. */
  chainageM: number;
  /** Sum of igv (initial grouting) in this bin, litres. */
  initialL: number;
  /** Sum of agv (additional grouting) in this bin, litres. */
  additionalL: number;
  /** Latest actVol (cumulative m^3) seen in this bin. */
  actVolM3: number | null;
  /** Latest desVol (target m^3) seen in this bin. */
  desVolM3: number | null;
  /** Mean skin friction (kN/m) across events in this bin. */
  skfKnM: number | null;
  /** Number of events that fell in this bin. */
  eventCount: number;
}

export interface LubricationLatest {
  record: BentoniteRecord | null;
  /** Cutterhead chainage at the record's timestamp, if known. */
  cutterheadChainageM: number | null;
  /** Seconds since the latest record (server clock − record.ts). */
  lagSeconds: number | null;
  /** Total events stored in our local DB. */
  recordsTotal: number;
  /** How many of those have any non-zero numeric field. */
  nonZeroRecordsTotal: number;
  /** ISO timestamp of the latest event in the DB (any). */
  latestRecordTs: string | null;
}

// ─────────────────────────────────────────────────────────────────────
// Drive geometry — pipe schedule + box positioning. Kept here since the
// drive table already has columns for it and the JF / future features
// may use it; not currently consumed by the rebuilt Lubrication page.
// ─────────────────────────────────────────────────────────────────────

export interface PipeSchedule {
  faceToTbmBoxM: number | null;
  tbmBoxToFirstPipeBoxM: number | null;
  pipeBoxPitchM: number | null;
  pipeLengthM: number | null;
  overcutMm: number | null;
}

export const DEFAULT_PIPE_SCHEDULE: PipeSchedule = {
  faceToTbmBoxM: null,
  tbmBoxToFirstPipeBoxM: null,
  pipeBoxPitchM: null,
  pipeLengthM: null,
  overcutMm: null,
};

export function distanceFromFaceM(
  boxNo: number,
  schedule: PipeSchedule,
): number | null {
  const x1 = schedule.faceToTbmBoxM;
  const x2 = schedule.tbmBoxToFirstPipeBoxM;
  const dx = schedule.pipeBoxPitchM;
  if (x1 == null) return null;
  if (boxNo <= 1) return x1;
  if (x2 == null) return null;
  if (boxNo === 2) return x1 + x2;
  if (dx == null) return null;
  return x1 + x2 + (boxNo - 2) * dx;
}
